/**
 * Geldbeträge deutsch schreiben — an genau einer Stelle.
 *
 * ## Warum das eine eigene Datei ist
 *
 * Es gab drei davon, und sie schrieben denselben Betrag verschieden:
 *
 * | Ort | `24.5` wurde zu | `25` wurde zu |
 * |---|---|---|
 * | `VorbereitungPanel` (`euro(n, stellen)`) | `24,50 €` | `25,00 €` |
 * | `LadePanel` (`style: 'currency'`) | `24,50 €` | `25,00 €` |
 * | `TagesplanPanel` (`maximumFractionDigits: 2`) | `24,5 €` | `25 €` |
 * | `TagesplanAnStation` (roh, `{p.kosten_eur} €`) | `24.5 €` | `25 €` |
 *
 * Die letzte Zeile ist der eigentliche Grund: Dort stand der rohe
 * JavaScript-Wert mit **Dezimalpunkt** in einer deutschen Oberfläche. Solange
 * jeder Reiter seinen eigenen Formatierer hat, fällt so etwas niemandem auf —
 * man sieht die beiden Zahlen nie nebeneinander.
 *
 * Mit der Kostenbetrachtung stehen sie nebeneinander: Der Reiter „Kosten"
 * bringt Plan aus der Vorbereitung, Strom aus dem Ladeplan und Tagesplanpreise
 * in eine Tabelle. Drei Schreibweisen in einer Spalte sind kein Schönheits-,
 * sondern ein Lesefehler — man hält `24,5` und `24,50` für zwei Werte.
 *
 * ## Warum ein geschütztes Leerzeichen
 *
 * Betrag und Währungszeichen gehören zusammen; ein Umbruch dazwischen lässt
 * das `€` allein in der nächsten Zeile stehen. In den schmalen Spalten der
 * Kostentabelle passiert das tatsächlich.
 *
 * ## Warum `gestaltung/` und nicht `lib/`
 *
 * Es ist eine Frage der Darstellung, keine der Rechnung. Der Rechenkern
 * (`lib/planner/`) läuft auf dem iPhone in JavaScriptCore und gibt Zahlen
 * heraus, keine Zeichenketten — dieselbe Trennung wie bei
 * `stationsart.VERKEHRSMITTEL_ZEICHEN`.
 */

/**
 * Geschütztes Leerzeichen — Betrag und Zeichen bleiben zusammen.
 *
 * Als Escape und nicht als getipptes Zeichen: Ein U+00A0 im Quelltext sieht
 * aus wie ein gewöhnliches Leerzeichen und wird beim nächsten Bearbeiten
 * unbemerkt zu einem. Dann bricht die Zeile wieder.
 */
const NBSP = '\u00A0';

/**
 * `24.5` → `24,50 €`.
 *
 * `stellen` ist die Zahl der Nachkommastellen und wird **nach oben wie nach
 * unten** erzwungen: Bei Geld ist die abgeschnittene Null keine Kürzung,
 * sondern eine andere Aussage. `24,50 €` ist ein Preis, `24,5 €` sieht aus wie
 * ein Messwert, und `25 €` wie eine Schätzung. Genau deshalb steht in der
 * einfachen Anzeigedichte `euro(n, 0)` und in der Expertenfassung
 * `euro(n, 2)` — die Rundung ist dort eine Entscheidung und kein Zufall der
 * Formatierung.
 *
 * Etwas, das keine Zahl ist, ergibt einen Gedankenstrich und nicht `NaN €`.
 * Eine fehlende Angabe ist eine Lücke; `NaN €` sieht aus wie ein kaputter
 * Rechenweg und schickt jemanden auf die Suche nach einem Fehler, den es
 * nicht gibt.
 */
export function euro(n: number, stellen = 2): string {
  if (!Number.isFinite(n)) return '–';
  return `${zahl(n, stellen)}${NBSP}€`;
}

/**
 * `0.187` → `0,19 €/km`.
 *
 * Für die Kennzahlen, die einen Betrag **je Einheit** angeben. Die Einheit
 * kommt als Text, weil es davon nur eine Handvoll gibt (`km`, `100 km`,
 * `Tag`) und eine Tabelle dafür mehr Pflege als Nutzen wäre.
 */
export function euroJe(n: number, einheit: string, stellen = 2): string {
  if (!Number.isFinite(n)) return '–';
  return `${zahl(n, stellen)}${NBSP}€/${einheit}`;
}

/**
 * `412` + `USD` → `412,00 USD`.
 *
 * Für die Zeile „davon 412,00 USD · 188,50 CAD" unter der Ist-Summe: Beträge,
 * die **nicht** in Euro bezahlt wurden, stehen dort in ihrer eigenen Währung.
 *
 * Der Code hinter der Zahl und kein Zeichen davor: `$` steht für den
 * US-Dollar wie für den kanadischen, und nebeneinander wären zwei Zeilen mit
 * `$` eine Verwechslung, die Geld kostet. Drei Buchstaben sind eindeutig.
 *
 * Immer zwei Nachkommastellen, auch in der einfachen Anzeigedichte: Es ist
 * der Betrag, der auf dem Beleg steht, und kein gerundeter Überblick.
 */
export function fremdbetrag(n: number, code: string): string {
  const wort = (code ?? '').trim().toUpperCase();
  if (!Number.isFinite(n) || !wort) return '–';
  return `${zahl(n, 2)}${NBSP}${wort}`;
}

/** Die deutsche Zahl ohne Währungszeichen — für beide Funktionen oben. */
function zahl(n: number, stellen: number): string {
  return n.toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen
  });
}
